/**
 * MONSTER TOWN — STARTER CONTENT. This file is the RECIPE every crew copies.
 *
 * Read this before you author a single placeable. Everything here is data: no
 * engine code was changed to add these, and none may be changed to add yours.
 *
 * THE RECIPE
 *  1. `definePlaceable({...})` — id, devName, category, scale, footprint, cost,
 *     stackable, walkable, buildArt, interact.
 *  2. `buildArt(ctx)` returns a Group centred on the footprint, floor at y=0.
 *     Use `src/render/kit.ts` for geometry and materials. Never `new Material`.
 *  3. `interact(ctx)` returns `{ feedback: [...] }` with AT LEAST ONE entry, and
 *     at least one of them must be something a kid can SEE (LAW #1). The
 *     compiler enforces non-empty; `npm run audit` enforces visible.
 *  4. Export the def and add it to the array in `src/content/index.ts`.
 */

import { FOOT_1x1, type Cell } from '../core/grid'
import { definePlaceable } from '../core/placeable'
import type { PlaceableId } from '../core/types'
import { boxOn, cylinder, group, inked, ball } from '../render/kit'

/** Plain wooden block. FREE FOREVER. The first thing every kid places. */
export const woodBlock = definePlaceable<{ knocks: number }>({
  id: 'block_wood' as PlaceableId,
  devName: 'Wooden block',
  category: 'block',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: true,
  walkable: false,
  tints: ['woodLight', 'wood', 'woodDark', 'wallMint', 'wallBlue', 'wallPink'],
  tags: ['starter'],
  initialState: () => ({ knocks: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('block_wood')
    const body = boxOn(t * 0.92, t * 0.92, t * 0.92, {
      color: ctx.tint ?? 'woodLight',
      name: 'body',
    })
    g.add(inked(body))
    // Little inset panel so a stack of blocks does not read as one grey slab.
    g.add(
      boxOn(t * 0.5, t * 0.5, t * 0.06, {
        color: 'wood',
        y: t * 0.21,
        z: t * 0.47,
        name: 'panel',
      }),
    )
    return g
  },

  // LAW #1: knock on it, it wobbles and thumps, and it remembers being knocked.
  // Every fifth knock throws stars, so a kid tapping repeatedly gets a payoff.
  interact: (ctx) => {
    const knocks = ctx.state.knocks + 1
    const lucky = knocks % 5 === 0
    return {
      feedback: [
        { kind: 'anim', clip: lucky ? 'bounce' : 'wobble', seconds: 0.4, target: 'body' },
        { kind: 'particles', effect: lucky ? 'stars' : 'dustPuff', count: lucky ? 8 : 3 },
        { kind: 'sound', cue: lucky ? 'chime_happy' : 'knock_wood' },
        { kind: 'state', patch: { knocks } },
      ],
      cooldownMs: 250,
    }
  },
})

/** Path tile. FREE FOREVER. Auto-connects to other paths (LAW #5). */
export const pathStone = definePlaceable<{ steps: number }>({
  id: 'path_stone' as PlaceableId,
  devName: 'Stone path',
  category: 'path',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: true,
  connect: { group: 'path', style: 'path' },
  tints: ['path', 'sand', 'rock'],
  tags: ['starter'],
  initialState: () => ({ steps: 0 }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('path_stone')
    g.add(boxOn(t, t * 0.08, t, { color: ctx.tint ?? 'path', name: 'slab' }))
    // Auto-connect: fill the gap toward each neighbouring path tile so the run
    // reads as one continuous path instead of separate squares.
    const nudge = t * 0.5
    const dirs: [number, number, number][] = [
      [1, 0, -nudge],
      [2, nudge, 0],
      [4, 0, nudge],
      [8, -nudge, 0],
    ]
    for (const [bit, dx, dz] of dirs) {
      if ((ctx.connect & bit) === 0) continue
      g.add(
        boxOn(t * 0.55, t * 0.07, t * 0.55, {
          color: ctx.tint ?? 'path',
          x: dx * 0.5,
          z: dz * 0.5,
          name: 'joint',
        }),
      )
    }
    return g
  },

  // LAW #1: a path is walkable, but tapping it still does something — a puff
  // of dust and a footstep, and it counts the steps for the town-life crew.
  interact: (ctx) => {
    const steps = ctx.state.steps + 1
    return {
      feedback: [
        { kind: 'particles', effect: 'dust', count: 4 },
        { kind: 'sound', cue: 'step_stone' },
        { kind: 'state', patch: { steps } },
      ],
      cooldownMs: 120,
    }
  },
})

/** A flower. FREE FOREVER. Smellable — the brief names this one explicitly. */
export const flowerPatch = definePlaceable<{ smelled: number; open: boolean }>({
  id: 'plant_flower' as PlaceableId,
  devName: 'Flower patch',
  category: 'plant',
  scale: 'both',
  footprint: FOOT_1x1,
  cost: 0,
  stackable: false,
  walkable: false,
  tints: ['flowerPink', 'flowerYellow', 'flowerPurple', 'flowerWhite', 'flowerRed'],
  tags: ['starter', 'cosy'],
  initialState: () => ({ smelled: 0, open: true }),

  buildArt: (ctx) => {
    const t = ctx.grid.tileSize
    const g = group('plant_flower')
    g.add(boxOn(t * 0.7, t * 0.05, t * 0.7, { color: 'grassDark', name: 'bed' }))
    const petal = ctx.tint ?? 'flowerPink'
    const heads = group('heads')
    const spots: [number, number, number][] = [
      [-0.18, 0.34, -0.12],
      [0.2, 0.42, 0.08],
      [0.02, 0.3, 0.22],
    ]
    for (const [x, h, z] of spots) {
      heads.add(cylinder(t * 0.03, t * h, { color: 'leaf', x: x * t, z: z * t }))
      heads.add(ball(t * 0.11, { color: petal, x: x * t, y: t * h, z: z * t }))
      heads.add(ball(t * 0.05, { color: 'flowerYellow', x: x * t, y: t * (h + 0.05), z: z * t }))
    }
    g.add(heads)
    return g
  },

  // LAW #1: smell the flower. It bobs, petals puff, the monster sniffs.
  interact: (ctx) => {
    const smelled = ctx.state.smelled + 1
    return {
      feedback: [
        { kind: 'monster', action: 'sniff', seconds: 1.1 },
        { kind: 'anim', clip: 'bob', seconds: 0.8, target: 'heads' },
        { kind: 'particles', effect: 'petals', count: 8 },
        { kind: 'sound', cue: 'sparkle_soft' },
        { kind: 'state', patch: { smelled } },
      ],
    }
  },
})

/** The demo target the harness uses. Not shipped content — a placement probe. */
export function demoLayout(): { defId: PlaceableId; anchor: Cell }[] {
  const out: { defId: PlaceableId; anchor: Cell }[] = []
  for (let i = 0; i < 6; i++) {
    out.push({ defId: pathStone.id, anchor: { gx: 6 + i, gy: 0, gz: 9 } })
  }
  out.push({ defId: woodBlock.id, anchor: { gx: 8, gy: 0, gz: 7 } })
  out.push({ defId: woodBlock.id, anchor: { gx: 9, gy: 0, gz: 7 } })
  out.push({ defId: woodBlock.id, anchor: { gx: 8, gy: 1, gz: 7 } })
  out.push({ defId: flowerPatch.id, anchor: { gx: 6, gy: 0, gz: 7 } })
  out.push({ defId: flowerPatch.id, anchor: { gx: 11, gy: 0, gz: 10 } })
  return out
}
